'use client';

import { useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { directApiClient } from '@/lib/api/direct-client';

/**
 * Reports a user's first genuine activity of each day (VALE-1921).
 *
 * Most regular users never sign in on a given morning: they come back to a
 * session that is still live, so the sign-in event alone misses exactly the
 * people using Vale most. This reports the return itself.
 *
 * **Only a person counts, never an open tab.** The trigger is a click, a key
 * press or a scroll-wheel turn in a visible tab. Nothing that runs on its own
 * (a poll, a token refresh, a tab left open across midnight or a weekend)
 * dispatches one of those, so an unattended tab can never report.
 *
 * **At most once per user per browser day.** A `localStorage` marker holds the
 * day already reported, so every later interaction costs one storage read and
 * no request. The backend is still the authority on "first of the day": it
 * dedupes across a user's devices and uses their office timezone, so a report
 * the browser thinks is new but the server has already seen writes nothing.
 *
 * **Staff impersonation never reports**, here and again on the server.
 */

const ACTIVITY_ENDPOINT = '/profile/activity-event';
const MARKER_KEY = 'vale_activity_reported';
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'wheel'] as const;
/** After a failed report, how long before an interaction may try again. */
const RETRY_AFTER_MS = 60_000;

/** The browser's local calendar date, `YYYY-MM-DD`. */
export function localDayKey(now: Date): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/** The marker is per user, so a second account in the same browser still reports. */
export function activityMarker(userKey: string, day: string): string {
  return `${userKey}|${day}`;
}

/**
 * Where storage is blocked (a private window, a locked-down profile), the
 * marker lives here instead, so the cost of no storage is at most one report
 * per page load rather than one per click.
 */
let memoryMarker: string | null = null;

function readMarker(): string | null {
  try {
    return window.localStorage.getItem(MARKER_KEY);
  } catch {
    return memoryMarker;
  }
}

function writeMarker(value: string | null): void {
  memoryMarker = value;
  try {
    if (value === null) {
      window.localStorage.removeItem(MARKER_KEY);
    } else {
      window.localStorage.setItem(MARKER_KEY, value);
    }
  } catch {
    // The in-memory copy above is the fallback.
  }
}

export function DailyActivityReporter() {
  const { data: session, status } = useSession();
  const userKey = session?.user?.id || session?.user?.email || null;
  const shouldListen =
    status === 'authenticated' && !session?.isImpersonating && !!userKey;

  useEffect(() => {
    if (!shouldListen || !userKey) {
      return;
    }

    let inFlight = false;
    let lastFailureAt = 0;

    const onInteraction = () => {
      if (inFlight || document.visibilityState !== 'visible') {
        return;
      }
      const marker = activityMarker(userKey, localDayKey(new Date()));
      if (
        readMarker() === marker ||
        Date.now() - lastFailureAt < RETRY_AFTER_MS
      ) {
        return;
      }

      // Written before the request, so a burst of clicks sends one report.
      inFlight = true;
      writeMarker(marker);
      directApiClient
        .post<void>(ACTIVITY_ENDPOINT)
        .catch(() => {
          // Let a later interaction try again, but not the very next click.
          lastFailureAt = Date.now();
          writeMarker(null);
        })
        .finally(() => {
          inFlight = false;
        });
    };

    for (const type of INTERACTION_EVENTS) {
      window.addEventListener(type, onInteraction, {
        capture: true,
        passive: true
      });
    }
    return () => {
      for (const type of INTERACTION_EVENTS) {
        window.removeEventListener(type, onInteraction, { capture: true });
      }
    };
  }, [shouldListen, userKey]);

  return null;
}
